/**
 * Default reference equality for selector projections.
 *
 * @param left First selected value.
 * @param right Second selected value.
 * @returns Whether both values are the same reference or primitive.
 */
export const referenceEqual = <T>( left: T, right: T ): boolean => {
    return Object.is( left, right );
};

/**
 * Shallow equality for plain objects and arrays (one level).
 *
 * @param left First value.
 * @param right Second value.
 * @returns Whether both values are shallow-equal.
 */
export const shallowEqual = <T>( left: T, right: T ): boolean => {
    if ( Object.is( left, right ) ) {
        return true;
    }

    if ( left === null || right === null || typeof left !== "object" || typeof right !== "object" ) {
        return false;
    }

    if ( Array.isArray( left ) && Array.isArray( right ) ) {
        if ( left.length !== right.length ) {
            return false;
        }

        for ( let index = 0; index < left.length; index += 1 ) {
            if ( !Object.is( left[index], right[index] ) ) {
                return false;
            }
        }

        return true;
    }

    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const leftKeys = Object.keys( leftRecord );
    const rightKeys = Object.keys( rightRecord );

    if ( leftKeys.length !== rightKeys.length ) {
        return false;
    }

    for ( const key of leftKeys ) {
        if ( !Object.is( leftRecord[key], rightRecord[key] ) ) {
            return false;
        }
    }

    return true;
};
